import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Autocomplete, Box, Button, ButtonGroup, Checkbox, Chip, Container, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";
import LazyLoader from '../components/LazyLoader';
import SearchField from "../components/SearchField";
import useSnackbar from "../components/snackbar/useSnackBar";
import SortingBar from '../components/SortingBar';
import StyledTab from '../components/TabPanel/StyledTab';
import StyledTabs from '../components/TabPanel/StyledTabs';
import { useApi } from '../hooks/useApi';
import { useLanguage } from '../hooks/useLanguage';
import { IFC_CLASS_TYPES } from '../utils/lib';

export default function ProductOverview({ userInfo }) {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [endReached, setEndReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [productsUpdater, setProductsUpdater] = useState(0);
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [selectedFilterType, setSelectedFilterType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  // sorting-states
  const [sortType, setSortType] = useState('MODIFICATION_DATE');
  const [sortDir, setSortDir] = useState('DESC');

  const api = useApi();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') ?? (userInfo?.isPaying ? 'private' : 'public');
  const accessFlags = useMemo(() => {
    switch (selectedTab) {
      case 'private':
        return { public: true, all: true };
      case 'public':
        return { public: true, all: false };
      case 'additional':
        return { public: false, all: true };
    }
  }, [selectedTab]);

  // favorites (realized with pagination)
  const [productFavorites, setProductFavorites] = useState([]);
  const [pageFavorites, setPageFavorites] = useState(1);
  const [countFavorites, setCountFavorites] = useState(0);
  const [pageSizeFavorites, setPageSizeFavorites] = useState(5);
  const [favoritesUpdater, setFavoritesUpdater] = useState(0);
  // state of the delete-prompt
  const [isOpen, setIsOpen] = useState({ state: false, id: null });

  const navigate = useNavigate();

  // locale for language
  const { locale } = useLanguage();

  const bulkFileRef = useRef();

  const intl = useIntl();

  const assetTypes = [
    {
      id: 2,
      name: intl.formatMessage({ id: 'Product.material' }),
      param: "MATERIAL"
    },
    {
      id: 3,
      name: intl.formatMessage({ id: 'Product.model' }),
      param: "MODEL"
    },
    {
      id: 4,
      name: intl.formatMessage({ id: "Product.color" }),
      param: "COLOR"
    }
  ]

  /**
   * The ifc-elements to filter for.
   */
  const ifcClasses = useMemo(() => {
    return Object.keys(IFC_CLASS_TYPES).map((el, index) => ({
      id: index,
      name: el,
      param: el
    }));
  }, [locale]);

  /**
   * Fetches paginated favorite products.
   */
  useEffect(() => {
    let active = true;
    api
      .get(`/product?page=${pageFavorites - 1}&size=${pageSizeFavorites}&favorites=true&sortType=NAME&sortDir=ASC`)
      .then(function (response) {
        if (!active) return;
        setProductFavorites(response.data.content);
        setCountFavorites(response.data.totalPages);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          navigate('/login');
        }
      });
    return () => {
      active = false;
    }
  }, [navigate, pageFavorites, pageSizeFavorites, favoritesUpdater]);

  /**
   * Handles fetching of product-pages from page 2 onwards.
   */
  useEffect(() => {
    if (page === 1 || endReached) return;
    fetchPage(page, query, selectedManufacturer, selectedFilterType, selectedCategory, selectedAttributes,
      accessFlags, sortType, sortDir);
  }, [page]);

  /**
   * Fetches paginated products with filter if specified (only first page).
   */
  useEffect(() => {
    setPage(1);
    fetchPage(1, query, selectedManufacturer, selectedFilterType, selectedCategory, selectedAttributes,
      accessFlags, sortType, sortDir);
  }, [query, selectedManufacturer, selectedCategory, selectedAttributes, accessFlags, productsUpdater, sortType, sortDir]);

  /**
   * Fetches the specified page of products with the filters applied.
   * @param {*} page 
   * @param {*} query 
   * @param {*} selectedManufacturer 
   * @param {*} selectedFilterType 
   * @param {*} selectedCategory 
   * @param {*} selectedAttributes 
   * @param {*} accessFlags
   * @param {*} sortType 
   * @param {*} sortDir 
   */
  const fetchPage = (page, query, selectedManufacturer, selectedFilterType, selectedCategory,
    selectedAttributes, accessFlags, sortType, sortDir) => {
    let fetchURL = '/product';
    fetchURL += `?page=${page - 1}&size=10`;
    if (query) {
      fetchURL += `&search=${query}`
    }
    if (selectedManufacturer) {
      fetchURL += `&manufacturers=${selectedManufacturer}`;
    }
    if (selectedFilterType !== '' && selectedCategory !== '') {
      switch (selectedFilterType) {
        case 1:
          fetchURL += '&ifcClass=' + categoryOptions.find(x => x.id === selectedCategory).param;
          break;
        case 2:
          fetchURL += '&assetTypes=' + categoryOptions.find(x => x.id === selectedCategory).param;
          break;
        default:
          fetchURL += `&${getCategoryParamName(selectedFilterType)}=${selectedCategory}`
      }
    };
    if (selectedAttributes.length > 0) {
      fetchURL += '&attributeKeys=';
      selectedAttributes.forEach((at, i, arr) => {
        fetchURL += at;
        if (i < arr.length - 1) {
          fetchURL += ',';
        }
      });
    }
    fetchURL += `&published=${accessFlags.public}&all=${accessFlags.all}&sortType=${sortType}&sortDir=${sortDir}`;
    setIsLoading(true);
    api
      .get(fetchURL)
      .then(function (response) {
        if (page === 1) {
          setProducts(response.data.content);
        } else {
          setProducts(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Loads manufacturers.
   */
  useEffect(() => {
    let active = true;
    api
      .get(`/manufacturer/all`)
      .then(response => {
        if (!active) return;
        if (response?.data) {
          setManufacturerOptions(response.data);
        } else {
          setManufacturerOptions([]);
        }
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      });
  }, []);

  /**
   * Loads attributes.
   */
  useEffect(() => {
    let active = true;
    api
      .get(`/attribute/all?lang=${locale}`)
      .then(response => {
        if (!active) return;
        setAttributeOptions(response.data);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      });
    return () => {
      active = false;
    };
  }, [locale])

  const handleSearch = (value) => {
    setQuery(value);
  };

  const handleChangeManufacturer = (event) => {
    setSelectedManufacturer(event.target.value);
  }

  const handleChangeAttributes = (value) => {
    setSelectedAttributes(value);
  }

  const handleClearFilter = () => {
    setSelectedManufacturer('');
    setSelectedFilterType('');
    setSelectedCategory('');
    setSelectedAttributes([]);
    setCategoryOptions([]);
  }

  const handleChangeFilterType = (event) => {
    setSelectedFilterType(event.target.value);
    setSelectedCategory('');
    setCategoryOptions([]);
    let fetchURL;
    switch (event.target.value) {
      case 0:
        fetchURL = `/object/all?published=true&all=true&lang=${locale}`;
        break;
      case 1:
        setCategoryOptions(ifcClasses);
        break;
      case 2:
        setCategoryOptions(assetTypes);
        break;
      default:
    }
    if (fetchURL) {
      api
        .get(fetchURL)
        .then(response => {
          if (response?.data) {
            setCategoryOptions(response.data);
          } else {
            setCategoryOptions([]);
          }
        })
        .catch(error => {
          console.log(error);
          if (error?.response?.status === 403) {
            navigate('/login');
          }
        });
    }
  };

  const handleChangeCategory = (event) => {
    setSelectedCategory(event.target.value);
  };

  const getCategoryName = (value) => {
    switch (value) {
      case 0:
        return intl.formatMessage({ id: "Product.object" });
      case 1:
        return intl.formatMessage({ id: "Product.ifcClass" });
      case 2:
        return intl.formatMessage({ id: "Product.assetType" });
      default:
        return '';
    }
  };

  const getCategoryParamName = (value) => {
    switch (value) {
      case 0:
        return 'objects';
      case 1:
        return 'ifcClass';
      case 2:
        return 'assetTypes';
      default:
        return '';
    }
  };

  // handles opening of deletion-prompt
  const handleOpen = (id) => {
    setIsOpen({ state: true, id: id });
  };

  // handles closing of deletion-prompt
  const handleClose = () => {
    setIsOpen({ state: false, id: null });
  };

  /**
 * Should be called when the sorting-type is changed.
 * @param {*} value 
 */
  const handleChangeSortType = (value) => {
    setSortType(value);
  }

  /**
   * Should be called when the sorting-direction is changed.
   * @param {*} value 
   */
  const handleChangeSortDir = (value) => {
    setSortDir(value);
  }

  /**
   * Handles the change of the selected tab.
   * @param {*} selectedTab 
   * @returns 
   */
  const handleChangeTab = (_, newValue) => {
    setSearchParams({ tab: newValue });
  };

  // snackbar for when deletion is not successful
  const { openSnackBar, closeSnackBar } = useSnackbar();

  // dialog that prompts a delete
  const dialog = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Product.deletionQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpen.state}
        handleAccept={() => handleDelete(isOpen.id)}
        handleClose={handleClose}
      />
    );
    if (isOpen.state) {
      return d;
    }
  }, [isOpen]);

  // deletes a product
  function handleDelete(id) {
    api
      .delete(`/product/${id}`)
      .then((response) => {
        // handle the response
        handleClose();
        setProductsUpdater(old => old + 1);
        setFavoritesUpdater(old => old + 1);
      })
      .catch((error) => {
        // handle errors
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        } else {
          handleClose();
          openSnackBar(intl.formatMessage({ id: "Overview.deletionFailure" }));
        }
      });
  }

  const handleFavoriteChange = (product) => (event) => {
    setProducts(old => old.map(p => p.id === product.id ? { ...p, isFavorite: event.target.checked } : p));
    api
      .put(`/product/${product.id}/favorite?value=${event.target.checked}`)
      .then(response => {
        // reload favorites
        setPageFavorites(1);
        setFavoritesUpdater(old => old + 1);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
  };

  /**
   * Parses the specified list of products and returns a TableRow.
   * @param {*} products 
   * @returns 
   */
  const getProductList = (products) => {
    return products.map((product) => (
      <TableRow
        key={product.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell component="th" scope="row">
          {product.name}
        </TableCell>
        <TableCell align="right">{product.description}</TableCell>
        <TableCell align="right">{product.productId}</TableCell>
        <TableCell align="right">{product.manufacturer?.name}</TableCell>
        <TableCell sx={{ paddingTop: 0, paddingBottom: 0 }} align="right">
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Checkbox icon={<StarBorderIcon />} checkedIcon={<StarIcon />} checked={product.isFavorite} onChange={handleFavoriteChange(product)} />
            <ButtonGroup
              variant="contained"
              aria-label="outlined primary button group"
            >
              <Button
                color="primary"
                aria-label="edit"
                onClick={() => navigate(`/product/${product.id}`, { state: { fromTab: selectedTab } })}
              >
                <EditIcon />
              </Button>
              {(userInfo?.isAdmin || product.ownerId === userInfo?.ownerId) && <Button
                component={Link}
                onClick={() => handleOpen(product.id)}
                color="error"
                aria-label="delete"
              >
                <DeleteIcon />
              </Button>}
            </ButtonGroup>
          </Box>
        </TableCell>
      </TableRow>
    ))
  };

  const productFavoritesList = useMemo(() => {
    return getProductList(productFavorites);
  }, [productFavorites]);

  const productList = useMemo(() => {
    return getProductList(products);
  }, [products]);

  /**
     * Uploads the selected zip-file to the server and increments the update-counter in case of success.
     * @param {*} files 
     * @returns 
     */
  const handleBulkFileSelect = (files) => {
    const file = files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    api.post("/product/bulk", formData)
      .then(() => {
        setProductsUpdater(prev => prev + 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  return (
    <Container>
      <input accept=".zip" ref={bulkFileRef} hidden type="file" onChange={event => handleBulkFileSelect(event?.target?.files)} onClick={event => event.target.value = ''} />
      <Button
        variant="contained"
        color="success"
        component={Link}
        to="/product/new"
        sx={{ float: "right" }}
      >
        <FormattedMessage id="Overview.create" />
      </Button>
      <Button
        variant="contained"
        color="yellow"
        onClick={() => bulkFileRef.current?.click()}
        sx={{ float: "right", marginRight: 2 }}
      >
        <FormattedMessage id="Overview.bulkUpload" />
      </Button>
      <Button
        variant="contained"
        color="secondary"
        component={Link}
        to="/import"
        sx={{ float: "right", marginRight: 2 }}
      >
        <FormattedMessage id="Product.import" />
      </Button>
      <h3><FormattedMessage id="Product.heading" /></h3>

      <h3><FormattedMessage id="Product.favorites" /></h3>
      <Pagination
        size="large"
        count={countFavorites}
        page={pageFavorites}
        sx={{ marginTop: '5px', marginBottom: '5px' }}
        onChange={(event, value) => setPageFavorites(value)}
      />

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage id="Product.name" /></TableCell>
              <TableCell align="right"><FormattedMessage id="Product.description" /></TableCell>
              <TableCell align="right"><FormattedMessage id="Product.articleNumber" /></TableCell>
              <TableCell align="right"><FormattedMessage id="Product.manufacturer" /></TableCell>
              <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productFavoritesList}
          </TableBody>
        </Table>
      </TableContainer>

      <h3><FormattedMessage id="Product.allProducts" /></h3>

      <Box sx={{ width: '100%', marginBottom: 1 }}>
        <StyledTabs
          value={selectedTab}
          onChange={handleChangeTab}
          aria-label="style data tabs"
        >
          {userInfo?.isPaying && <StyledTab value='private' label={intl.formatMessage({ id: "Product.filterPrivate" })} />}
          <StyledTab value='public' label={intl.formatMessage({ id: "Product.filterPublic" })} />
          {userInfo?.isAdmin && <StyledTab value='additional' label={intl.formatMessage({ id: "Product.filterPrivateOthers" })} />}
        </StyledTabs>
      </Box>

      <SearchField onChange={(value) => handleSearch(value)} />
      <div style={{ display: 'flex', alignItems: 'center' }} >
        <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
          <InputLabel id="manufacturer-select-standard-label"><FormattedMessage id="Product.manufacturer" /></InputLabel>
          <Select
            labelId="manufacturer-select-standard-label"
            id="manufacturer-select-standard"
            value={selectedManufacturer}
            onChange={handleChangeManufacturer}
            label={intl.formatMessage({ id: "Product.manufacturer" })}
          >
            <MenuItem value="">
              <em><FormattedMessage id="Product.none" /></em>
            </MenuItem>
            {manufacturerOptions.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
          <InputLabel id="filter-type-select-standard-label"><FormattedMessage id="Product.filterType" /></InputLabel>
          <Select
            labelId="filter-type-select-standard-label"
            id="filter-type-select-standard"
            value={selectedFilterType}
            onChange={handleChangeFilterType}
            label={intl.formatMessage({ id: "Product.filterType" })}
          >
            <MenuItem value="">
              <em><FormattedMessage id="Product.none" /></em>
            </MenuItem>
            <MenuItem value={0}><FormattedMessage id="Product.object" /></MenuItem>
            <MenuItem value={1} ><FormattedMessage id="Product.ifcClass" /></MenuItem>
            <MenuItem value={2}><FormattedMessage id="Product.assetType" /></MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
          <InputLabel id="filter-value-select-standard-label">{getCategoryName(selectedFilterType)}</InputLabel>
          <Select
            labelId="filter-value-select-standard-label"
            id="filter-value-select-standard"
            value={selectedCategory}
            onChange={handleChangeCategory}
            label={getCategoryName(selectedFilterType)}
          >
            {categoryOptions.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Autocomplete
          sx={{ m: 1, width: '100%' }}
          multiple
          id="tags-filled"
          options={attributeOptions.map(at => at.key)}
          defaultValue={[]}
          value={selectedAttributes}
          freeSolo
          onChange={(event, value) => handleChangeAttributes(value)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label={intl.formatMessage({ id: "Product.attributes" })}
              placeholder={intl.formatMessage({ id: "Product.attributes" })}
            />
          )}
        />
        <Button
          variant="contained"
          color="error"

          sx={{ float: "right" }}
          style={{ marginLeft: 'auto' }}
          onClick={handleClearFilter}
        >
          <ClearSharpIcon />
        </Button>
      </div>
      <SortingBar onChangeSortType={handleChangeSortType} onChangeSortDir={handleChangeSortDir}
        defaultSortType='MODIFICATION_DATE' defaultSortDir='DESC' />
      <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0, marginTop: 1 }}>
        <LazyLoader
          onLoad={() => setPage(prev => prev + 1)}
          loading={isLoading}
          endReached={endReached}
        >
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell><FormattedMessage id="Product.name" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Product.description" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Product.articleNumber" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Product.manufacturer" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productList}
              </TableBody>
            </Table>
          </TableContainer>
        </LazyLoader>
      </Box>
      {dialog}
    </Container>
  );
}
